import { useEffect, useRef, useState } from "react";
import { formatDate, formatTime } from "@/lib/format";
import { ChevronLeft, ChevronRight, Check, Users } from "lucide-react";

const SessionRow = ({ s, i, checked, free, disabled, onToggle }) => (
  <button type="button" disabled={!free || disabled} onClick={onToggle} data-testid={`session-${s.id}`}
    className={`w-full text-left rounded-xl border p-3 flex items-center gap-3 transition-colors ${checked ? "border-primary bg-primary/5" : "border-border"} ${free && !disabled ? "hover:border-primary/60" : ""} ${disabled && !checked ? "opacity-50" : ""}`}>
    {free && (
      <span className={`grid place-items-center w-5 h-5 rounded border shrink-0 ${checked ? "bg-primary border-primary text-primary-foreground" : "border-border"}`}>
        {checked && <Check className="w-3.5 h-3.5" />}
      </span>
    )}
    <span className="flex-1 min-w-0">
      <span className="block text-sm font-medium capitalize">Sesión {i + 1} · {formatDate(s.starts_at)}</span>
      <span className="block text-xs text-muted-foreground">{formatTime(s.starts_at)}{free ? ` · ${s.spots_left > 0 ? `${s.spots_left} cupos` : "Agotada"}` : ""}</span>
    </span>
  </button>
);

export function GroupSessionPicker({ w, value, onChange }) {
  const groups = w.groups || [];
  const free = w.enrollment_mode === "free";
  const max = Math.min(w.max_sessions || 1, groups.reduce((n, x) => n + x.sessions.filter((s) => s.spots_left > 0).length, 0)) || 1;
  const [idx, setIdx] = useState(0);
  const tabsRef = useRef(null);
  useEffect(() => {
    const box = tabsRef.current;
    const el = box?.children[idx];
    if (box && el) box.scrollTo({ left: el.offsetLeft - (box.clientWidth - el.offsetWidth) / 2, behavior: "smooth" });
  }, [idx]);
  useEffect(() => {
    const i = groups.findIndex((g) => g.id === value.groupId);
    if (i >= 0) setIdx(i);
  }, [value.groupId]); // eslint-disable-line
  if (!groups.length) return <p className="text-sm text-muted-foreground">No hay sesiones programadas.</p>;
  const g = groups[Math.min(idx, groups.length - 1)];
  const picked = value.sessionIds || [];
  const toggle = (s) => {
    const has = picked.includes(s.id);
    if (!has && picked.length >= max) return;
    onChange({ ...value, sessionIds: has ? picked.filter((x) => x !== s.id) : [...picked, s.id] });
  };
  const chosen = !free && value.groupId === g.id;
  return (
    <div className="space-y-2" data-testid="group-session-picker">
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => setIdx(idx - 1)} disabled={idx === 0} aria-label="Grupo anterior" data-testid="group-prev"
          className="grid place-items-center w-8 h-8 rounded-full border disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
        <div ref={tabsRef} className="relative flex-1 w-0 min-w-0 flex gap-1.5 overflow-x-auto no-scrollbar scroll-smooth" data-testid="group-tabs">
          {groups.map((x, i) => (
            <button key={x.id} type="button" onClick={() => setIdx(i)} data-testid={`group-tab-${x.id}`}
              className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${i === idx ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>
              {x.name}{!free && value.groupId === x.id ? " ✓" : ""}
            </button>
          ))}
        </div>
        <button type="button" onClick={() => setIdx(idx + 1)} disabled={idx >= groups.length - 1} aria-label="Grupo siguiente" data-testid="group-next"
          className="grid place-items-center w-8 h-8 rounded-full border disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
      </div>
      {groups.length > 1 && <p className="text-[11px] text-center text-muted-foreground" data-testid="group-position">{g.name} · {idx + 1} de {groups.length} grupos</p>}
      <div className="space-y-2 max-h-56 overflow-y-auto pr-1" data-testid="session-list">
        {g.sessions.map((s, i) => (
          <SessionRow key={s.id} s={s} i={i} free={free} checked={free ? picked.includes(s.id) : chosen}
            disabled={free && (s.spots_left <= 0 || (!picked.includes(s.id) && picked.length >= max))} onToggle={() => toggle(s)} />
        ))}
      </div>
      {free ? (
        <p className="text-xs text-muted-foreground" data-testid="sessions-picked-count">
          Selecciona {max} {max === 1 ? "sesión" : "sesiones"} de cualquier grupo
        </p>
      ) : (
        <button type="button" onClick={() => onChange({ ...value, groupId: g.id })} disabled={g.spots_left <= 0} data-testid={`choose-group-${g.id}`}
          className={`w-full rounded-xl border-2 p-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${chosen ? "border-primary bg-primary/10 text-primary" : "border-dashed border-primary/50 text-primary hover:bg-primary/5"} disabled:opacity-50`}>
          {chosen ? <><Check className="w-4 h-4" />{g.name} elegido · {g.sessions.length} sesiones</> : g.spots_left <= 0 ? `${g.name} agotado` : <><Users className="w-4 h-4" />Elegir {g.name} ({g.spots_left} cupos)</>}
        </button>
      )}
    </div>
  );
}
